import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { Input } from 'reactstrap';
import { TypedMemo } from '../../common/types';
import { errorAlertMessage } from '../../common/views/ErrorAlert/errorAlertMessage';
import { ErrorAPIResult } from '../../../redux/api/utils/isSuccessfulResult';
import { IntegrationTypeIcon } from '../components/IntegrationTypeIcon/IntegrationTypeIcon';
import { canSavePastedLink, pastedLinkProblem } from './serviceLinkEdit';
import { MARK_ICON_SIZE, PanelService } from './servicesPanelServices';
import { servicesPanelMessages as m } from './servicesPanelMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for ServicesPanel; each part uses its own slice of it */
import styles from './ServicesPanel.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ServiceLinkEditorProps {
  service: PanelService;
  /** The link of the row now, or null (Paste link). */
  url: string | null;
  isSaving: boolean;
  /** The error of the last save (a 400 of the server shows its message). */
  error: ErrorAPIResult['error'] | undefined;
  /** A new address, or null to remove the link. */
  onSave: (url: string | null) => void;
  onCancel: () => void;
}

/**
 * Paste or edit the link in the row (design d5). One row opens at a time. Enter saves, Escape
 * cancels. Save stays off while the check fails: the address of another service, or an album page.
 */
const ServiceLinkEditorComponent: React.FC<ServiceLinkEditorProps> = ({
  service,
  url,
  isSaving,
  error,
  onSave,
  onCancel,
}) => {
  const intl = useIntl();
  const [value, setValue] = useState(url ?? '');
  const problem = pastedLinkProblem(service.integrationType, value);
  const canSave = canSavePastedLink(service.integrationType, value) && !isSaving;
  const save = useCallback(() => {
    if (canSave) {
      onSave(value.trim());
    }
  }, [canSave, onSave, value]);
  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        save();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        onCancel();
      }
    },
    [onCancel, save]
  );

  let problemText: string | null = null;
  if (problem?.kind === 'otherService') {
    problemText =
      problem.service === ''
        ? intl.formatMessage(m.pasteOtherServiceUnknown, { service: service.name })
        : intl.formatMessage(m.pasteOtherService, { other: problem.service, service: service.name });
  } else if (problem?.kind === 'album') {
    problemText = intl.formatMessage(m.pasteAlbum);
  } else if (error !== undefined) {
    problemText = errorAlertMessage(intl, error);
  }

  return (
    <li className={clsx(styles.row, styles.rowEdit)} data-testid={`service-row-edit-${service.integrationType}`}>
      <span className={styles.mark}>
        <IntegrationTypeIcon integrationType={service.integrationType} size={MARK_ICON_SIZE} square />
      </span>
      <div className={styles.field}>
        <b className={styles.name}>{service.name}</b>
        <div className={styles.fieldRow}>
          <Input
            type="url"
            autoFocus
            value={value}
            disabled={isSaving}
            invalid={problem !== null}
            aria-label={service.name}
            placeholder={intl.formatMessage(m.pastePlaceholder, { service: service.name })}
            onChange={(event) => setValue(event.target.value)}
            onKeyDown={onKeyDown}
          />
          <button type="button" className="btn btn-sm btn-primary" disabled={!canSave} onClick={save}>
            {intl.formatMessage(m.save)}
          </button>
          <button type="button" className={styles.link} onClick={onCancel}>
            {intl.formatMessage(m.cancelEdit)}
          </button>
        </div>
        {problemText !== null && <span className={styles.error}>{problemText}</span>}
        {problemText === null && value.trim() === '' && (
          <span className={styles.hint}>{intl.formatMessage(m.pasteHint, { service: service.name })}</span>
        )}
        {url !== null && (
          <button
            type="button"
            className={clsx(styles.link, styles.linkDanger)}
            disabled={isSaving}
            onClick={() => onSave(null)}
          >
            {intl.formatMessage(m.remove)}
          </button>
        )}
      </div>
      <span />
    </li>
  );
};

export const ServiceLinkEditor = TypedMemo(ServiceLinkEditorComponent);
